import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useSolitaireSettings, SolitaireSettingsProvider } from "@/context/SolitaireSettingsContext";
import { computeSolitairePrice, fmtINR } from "@/lib/solitairePricing";

function PrintInner() {
  const { stock_id } = useParams();
  const [item, setItem] = useState(null);
  const { usdRate, markupKey, gstOn } = useSolitaireSettings();
  const price = useMemo(() => item ? computeSolitairePrice(item, usdRate, markupKey, gstOn) : null, [item, usdRate, markupKey, gstOn]);

  const verifyUrl = useMemo(() => {
    if (!item || !item.cert_no) return null;
    if (item.cert_lab === "IGI") return `https://www.igi.org/Verify-Your-Report/?r=${item.cert_no}`;
    if (item.cert_lab === "GIA") return `https://www.gia.edu/report-check?reportno=${item.cert_no}`;
    if (item.cert_lab === "HRD") return `https://my.hrdantwerp.com/?id=${item.cert_no}`;
    return null;
  }, [item]);

  useEffect(() => {
    api.get(`/solitaires/${stock_id}`).then(({ data }) => setItem(data)).catch(() => setItem(null));
  }, [stock_id]);

  useEffect(() => {
    if (item) setTimeout(() => window.print(), 600);
  }, [item]);

  if (!item) return <div className="p-10 text-center">Loading…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6 print:p-0" style={{ color: "#0B1A33" }}>
      <style>{`@media print { .no-print { display: none !important; } @page { margin: 8mm; size: A6 portrait; } body { background: white !important; } }`}</style>
      <div className="w-full max-w-[360px] border-2 p-6 print:border-0 print:max-w-none" style={{ borderColor: "#C9A84C" }}>
        <div className="text-center pb-4 border-b" style={{ borderColor: "rgba(201,168,76,0.4)" }}>
          <div className="font-display text-xl" style={{ fontWeight: 700, letterSpacing: "0.04em" }}>BHOLAA JEWELS</div>
          <div className="text-[8px] tracking-[0.25em] uppercase mt-1" style={{ color: "#A88934" }}>Est. 1966 · Chandigarh</div>
        </div>
        <div className="text-center pt-4 pb-3">
          <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "#A88934" }}>{item.cert_lab || "Certified"} Solitaire</div>
          <div className="font-display text-2xl mt-1" style={{ fontWeight: 600 }}>
            {Number(item.carat || 0).toFixed(2)} ct {item.shape || ""}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase mt-1" style={{ color: "rgba(11,26,51,0.6)" }}>
            {[item.colour, item.clarity, item.cut].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 py-3 text-[10px] border-t border-b" style={{ borderColor: "rgba(201,168,76,0.3)" }}>
          <Pair k="Stock" v={item.stock_id} />
          <Pair k="Cert No" v={item.cert_no} />
          <Pair k="Polish" v={item.polish} />
          <Pair k="Symmetry" v={item.symmetry} />
          <Pair k="Table" v={item.table_pct ? `${item.table_pct.toFixed(1)}%` : "—"} />
          <Pair k="Depth" v={item.depth_pct ? `${item.depth_pct.toFixed(1)}%` : "—"} />
          <Pair k="Fluor" v={item.fluorescence || "—"} />
          <Pair k="Meas" v={item.measurements || "—"} />
        </div>
        <div className="text-center py-4">
          <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "#A88934" }}>Price</div>
          <div className="font-display text-3xl mt-1" style={{ fontWeight: 700 }}>{price ? fmtINR(price.total) : "—"}</div>
          {gstOn && <div className="text-[9px] mt-1" style={{ color: "rgba(11,26,51,0.55)" }}>inclusive of 1.5% GST</div>}
        </div>
        {verifyUrl && (
          <div className="flex flex-col items-center pt-3 border-t" style={{ borderColor: "rgba(201,168,76,0.3)" }}>
            <QRCodeSVG value={verifyUrl} size={84} bgColor="#FFFFFF" fgColor="#0B1A33" level="M" />
            <div className="text-[8px] tracking-[0.25em] uppercase mt-2 text-center" style={{ color: "rgba(11,26,51,0.6)" }}>Scan to verify on {item.cert_lab}</div>
          </div>
        )}
        <div className="no-print mt-5 text-center">
          <button onClick={() => window.print()} className="px-4 py-2 text-xs tracking-widest uppercase" style={{ background: "#0B1A33", color: "#FAF6EE" }}>Print</button>
        </div>
      </div>
    </div>
  );
}

const Pair = ({ k, v }) => (
  <div className="flex justify-between">
    <span style={{ color: "rgba(11,26,51,0.55)" }}>{k}</span>
    <span style={{ color: "#0B1A33", fontWeight: 500 }}>{v || "—"}</span>
  </div>
);

export default function PrintSolitaire() {
  return <SolitaireSettingsProvider><PrintInner /></SolitaireSettingsProvider>;
}
